import { useState, useEffect } from "react";
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  where,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { db, appId } from "../config/firebase";
import {
  formatPropertyNotFoundError,
  formatPropertyReference,
} from "../utils/propertyPlatforms";
import {
  migratePropertiesToPlatformInfo,
  needsPlatformMigration,
} from "../utils/propertyMigration";
import { extractPropertyInfo } from "../utils/propertyPlatforms";
import { extractDistressFields } from "../services/propertyDataSchema";

const mergeNotesPreservingManual = (existingNotes = "", incomingNotes = "") => {
  const existing = existingNotes.trim();
  const incoming = incomingNotes.trim();

  // Only use incoming scraped notes when notes are empty.
  if (!existing) {
    return incoming;
  }

  // Nothing new to merge.
  if (!incoming) {
    return existing;
  }

  // Avoid duplicating the same scraped block repeatedly.
  if (existing.includes(incoming)) {
    return existing;
  }

  // If scraped notes are significantly richer (>10% longer), append at bottom.
  if (incoming.length > existing.length * 1.1) {
    return `${existing}\n\nScraped update:\n${incoming}`;
  }

  // Default: keep user notes as-is.
  return existing;
};

const mergeNotesForRefetch = (existingNotes = "", incomingNotes = "") => {
  const existing = (existingNotes || "").trim();
  const incoming = (incomingNotes || "").trim();

  if (!existing) {
    return incoming;
  }

  if (!incoming) {
    return existing;
  }

  if (existing.includes(incoming)) {
    return existing;
  }

  if (incoming.length > existing.length * 1.1) {
    return `${existing}\n\nScraped update:\n${incoming}`;
  }

  return existing;
};

export const useProperties = (userId, isAuthReady) => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthReady || !userId || !db) {
      if (isAuthReady && !userId) {
        setProperties([]);
      }
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    const propsPath = `artifacts/${appId}/users/${userId}/trackedProperties`;

    const unsubscribe = onSnapshot(
      query(collection(db, propsPath)),
      async (snap) => {
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        // Check if migration is needed and perform it
        if (needsPlatformMigration(data)) {
          try {
            const migratedCount = await migratePropertiesToPlatformInfo(
              userId,
              data
            );
            if (migratedCount > 0) {
              console.log(
                `Migrated ${migratedCount} properties with platform information`
              );
              // The onSnapshot will trigger again with updated data
              return;
            }
          } catch (error) {
            console.error("Migration failed:", error);
            // Continue with non-migrated data
          }
        }

        setProperties(data);
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error fetching properties:", err);
        setError("Could not fetch properties.");
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isAuthReady, userId]);

  const addProperty = async (propertyData) => {
    if (!db || !userId) {
      throw new Error("Database or user not available.");
    }

    const propertiesCollectionPath = `artifacts/${appId}/users/${userId}/trackedProperties`;
    const q = query(
      collection(db, propertiesCollectionPath),
      where("url", "==", propertyData.url.trim())
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Property exists, check if it's deleted and handle accordingly
      const existingPropertyDoc = querySnapshot.docs[0];
      const existingPropertyData = existingPropertyDoc.data();
      const existingPropertyId = existingPropertyDoc.id;

      // If property was deleted, undelete it and treat as new property
      if (existingPropertyData.deleted) {
        const mergedNotes = mergeNotesPreservingManual(
          existingPropertyData.notes || "",
          propertyData.notes || ""
        );

        const undeletePayload = {
          // Remove deletion markers
          deleted: false,
          deletedTimestamp: null,

          // Update with new data
          notes: mergedNotes,
          forSale: propertyData.forSale || false,
          forRent: propertyData.forRent || false,
          lastModifiedTimestamp: serverTimestamp(),

          // Add new history entry
          history: [
            {
              price: propertyData.price,
              reportedPublishedDate: propertyData.publishedDate || "N/A",
              reportedUpdateDate: propertyData.updatedDate || "N/A",
              snapshotTimestamp: Timestamp.now(),
            },
            ...(existingPropertyData.history || []),
          ],
          lastKnownPrice: propertyData.price,
          lastReportedUpdateDate: propertyData.updatedDate || "N/A",
        };

        // Update all additional fields
        if (propertyData.additionalFields) {
          Object.keys(propertyData.additionalFields).forEach((key) => {
            const value = propertyData.additionalFields[key];
            if (value !== undefined && value !== null && value !== "") {
              undeletePayload[key] = value;
            }
          });
        }

        await updateDoc(
          doc(db, propertiesCollectionPath, existingPropertyId),
          undeletePayload
        );
        return { isNew: false, hasUpdate: true, wasDeleted: true };
      }

      // Property exists and is not deleted - normal update logic
      const sortedHistory = (existingPropertyData.history || [])
        .slice()
        .sort(
          (a, b) =>
            (b.snapshotTimestamp?.toDate?.() || new Date(0)) -
            (a.snapshotTimestamp?.toDate?.() || new Date(0))
        );
      const latestHistoryEntry =
        sortedHistory.length > 0 ? sortedHistory[0] : null;
      const mergedNotes = mergeNotesPreservingManual(
        existingPropertyData.notes || "",
        propertyData.notes || ""
      );

      let priceChanged =
        !latestHistoryEntry || propertyData.price !== latestHistoryEntry.price;
      let updatedDateChanged =
        !latestHistoryEntry ||
        (propertyData.updatedDate &&
          propertyData.updatedDate !==
            (latestHistoryEntry.reportedUpdateDate || ""));
      let publishedDateChanged =
        !latestHistoryEntry ||
        (propertyData.publishedDate &&
          propertyData.publishedDate !==
            (latestHistoryEntry.reportedPublishedDate || ""));
      let notesChanged = mergedNotes !== (existingPropertyData.notes || "");
      let typeChanged =
        propertyData.forSale !== (existingPropertyData.forSale || false) ||
        propertyData.forRent !== (existingPropertyData.forRent || false);

      let hasNewSnapshotInfo =
        priceChanged || updatedDateChanged || publishedDateChanged;

      if (hasNewSnapshotInfo || notesChanged || typeChanged) {
        const updatePayload = {
          notes: mergedNotes,
          forSale: propertyData.forSale || false,
          forRent: propertyData.forRent || false,
          lastModifiedTimestamp: serverTimestamp(),
        };

        // Only add additional fields if they exist and are not undefined
        if (propertyData.additionalFields) {
          Object.keys(propertyData.additionalFields).forEach((key) => {
            const value = propertyData.additionalFields[key];
            if (value !== undefined && value !== null && value !== "") {
              updatePayload[key] = value;
            }
          });
        }

        if (hasNewSnapshotInfo) {
          const newHistorySnapshot = {
            price: propertyData.price,
            reportedPublishedDate: propertyData.publishedDate || "N/A",
            reportedUpdateDate: propertyData.updatedDate || "N/A",
            snapshotTimestamp: Timestamp.now(),
          };
          updatePayload.history = [newHistorySnapshot, ...sortedHistory];
          updatePayload.lastKnownPrice = propertyData.price;
          updatePayload.lastReportedUpdateDate =
            propertyData.updatedDate || "N/A";
        }

        await updateDoc(
          doc(db, propertiesCollectionPath, existingPropertyId),
          updatePayload
        );
        return { isNew: false, hasUpdate: hasNewSnapshotInfo };
      } else {
        return { isNew: false, hasUpdate: false };
      }
    } else {
      // New property
      // Extract platform information from URL
      const { propertyId, platform } = extractPropertyInfo(
        propertyData.url.trim()
      );

      const newPropData = {
        url: propertyData.url.trim(),
        propertyId: propertyId || null,
        platform: platform || null,
        title: (propertyData.additionalFields?.title || "").trim(),
        area: (propertyData.additionalFields?.area || "").trim(),
        bedrooms: (propertyData.additionalFields?.bedrooms || "").trim(),
        bathrooms: (propertyData.additionalFields?.bathrooms || "").trim(),
        startPrice: (propertyData.additionalFields?.startPrice || "").trim(),
        imageURL: (propertyData.additionalFields?.imageURL || "").trim(),
        latitude: propertyData.additionalFields?.latitude || null,
        longitude: propertyData.additionalFields?.longitude || null,
        pets: propertyData.additionalFields?.pets || "",
        furnishing: propertyData.additionalFields?.furnishing || "",
        parking: propertyData.additionalFields?.parking || "",
        constructionYear: propertyData.additionalFields?.constructionYear || "",
        onlineViewing: propertyData.additionalFields?.onlineViewing || "",
        landlordName: (
          propertyData.additionalFields?.landlordName || ""
        ).trim(),
        landlordJoined: (
          propertyData.additionalFields?.landlordJoined || ""
        ).trim(),
        ownerAdCount: (
          propertyData.additionalFields?.ownerAdCount || ""
        ).trim(),
        cityId: (propertyData.additionalFields?.cityId || "").trim(),
        notes: propertyData.notes || "",
        // Property type fields
        forSale: propertyData.forSale || false,
        forRent: propertyData.forRent || false,
        // Distressed-asset signals (mirrors propertyDataSchema.js)
        ...extractDistressFields(propertyData.additionalFields),
        firstTrackedTimestamp: serverTimestamp(),
        history: [
          {
            price: propertyData.price,
            reportedPublishedDate: propertyData.publishedDate || "N/A",
            reportedUpdateDate: propertyData.updatedDate || "N/A",
            snapshotTimestamp: Timestamp.now(),
          },
        ],
        lastKnownPrice: propertyData.price,
        lastReportedUpdateDate: propertyData.updatedDate || "N/A",
        lastModifiedTimestamp: serverTimestamp(),
        isFavorite: false,
      };

      await addDoc(collection(db, propertiesCollectionPath), newPropData);
      return { isNew: true, hasUpdate: false };
    }
  };

  const updateProperty = async (propertyId, updateData) => {
    if (!db || !userId) {
      throw new Error("Database or user not available.");
    }

    // Sanitize update data to remove undefined values
    const sanitizedData = {};
    Object.keys(updateData).forEach((key) => {
      const value = updateData[key];
      if (value !== undefined) {
        sanitizedData[key] = value === null ? null : value;
      }
    });

    await updateDoc(
      doc(
        db,
        `artifacts/${appId}/users/${userId}/trackedProperties`,
        propertyId
      ),
      sanitizedData
    );
  };

  const deleteProperty = async (propertyId) => {
    if (!db || !userId) {
      throw new Error("Database or user not available.");
    }

    await updateDoc(
      doc(
        db,
        `artifacts/${appId}/users/${userId}/trackedProperties`,
        propertyId
      ),
      { deleted: true, deletedTimestamp: serverTimestamp() }
    );
  };

  const toggleFavorite = async (propertyId, currentIsFavorite) => {
    if (!db || !userId) {
      throw new Error("Database or user not available.");
    }

    const propertyDocRef = doc(
      db,
      `artifacts/${appId}/users/${userId}/trackedProperties`,
      propertyId
    );

    await updateDoc(propertyDocRef, {
      isFavorite: !currentIsFavorite,
      lastModifiedTimestamp: serverTimestamp(),
    });
  };

  const refetchAllProperties = async (fetchPropertyData) => {
    if (!db || !userId || !properties.length) {
      return "No properties to re-fetch or database not ready.";
    }

    // First, clean up any existing undefined values in the database
    const cleanupBatch = writeBatch(db);
    let cleanupCount = 0;

    for (const prop of properties) {
      const needsCleanup = Object.values(prop).some(
        (value) => value === undefined
      );
      if (needsCleanup) {
        const cleanData = {};
        Object.keys(prop).forEach((key) => {
          if (key !== "id" && prop[key] !== undefined) {
            cleanData[key] = prop[key] === null ? null : prop[key] || "";
          }
        });

        const propDocRef = doc(
          db,
          `artifacts/${appId}/users/${userId}/trackedProperties`,
          prop.id
        );
        cleanupBatch.update(propDocRef, cleanData);
        cleanupCount++;
      }
    }

    if (cleanupCount > 0) {
      try {
        await cleanupBatch.commit();
        console.log(
          `Cleaned up ${cleanupCount} properties with undefined values`
        );
      } catch (error) {
        console.error("Error cleaning up properties:", error);
      }
    }

    const startTime = Date.now();
    let checkedCount = 0;
    let updatedCount = 0;
    const errorsEncountered = [];
    const updatedProperties = [];
    const batch = writeBatch(db);

    for (const prop of properties) {
      if (prop.deleted || Number(prop.lastKnownPrice) === 0) {
        checkedCount++;
        continue;
      }

      try {
        // fetchPropertyData now returns normalized data for any platform
        const fetchedData = await fetchPropertyData(prop.url);
        checkedCount++;

        if (fetchedData) {
          // All fields are already normalized - no platform-specific mapping needed
          const newPrice = fetchedData.price || 0;
          const newApiPublishedDt = fetchedData.publishedDate || "";
          const newApiUpdatedDt = fetchedData.updatedDate || "";
          const notesFromApi =
            (fetchedData.title ? `Title: ${fetchedData.title}\n` : "") +
            (fetchedData.description
              ? `Description: ${fetchedData.description}`
              : "");

          const sortedHistory = (prop.history || [])
            .slice()
            .sort(
              (a, b) =>
                (b.snapshotTimestamp?.toDate?.() || new Date(0)) -
                (a.snapshotTimestamp?.toDate?.() || new Date(0))
            );
          const latestHistoryEntry =
            sortedHistory.length > 0 ? sortedHistory[0] : null;

          let needsHistoryUpdate = false;
          if (!latestHistoryEntry) {
            needsHistoryUpdate = true;
          } else {
            if (newPrice !== latestHistoryEntry.price)
              needsHistoryUpdate = true;
            if (
              newApiUpdatedDt &&
              newApiUpdatedDt !== (latestHistoryEntry.reportedUpdateDate || "")
            )
              needsHistoryUpdate = true;
          }

          // Use property type from normalized data
          const forSale = fetchedData.forSale || false;
          const forRent = fetchedData.forRent || false;
          const needsTypeUpdate =
            prop.forSale !== forSale ||
            prop.forRent !== forRent ||
            prop.forSale === undefined ||
            prop.forRent === undefined;

          // Backfill distressed-asset signals (so badges render) even when the
          // price/type are unchanged.
          const distressFields = extractDistressFields(fetchedData, prop);
          const needsDistressUpdate =
            prop.sellerType !== distressFields.sellerType ||
            prop.isDistressed !== distressFields.isDistressed ||
            prop.currency !== distressFields.currency ||
            prop.priceOnRequest !== distressFields.priceOnRequest ||
            JSON.stringify(prop.distress || null) !==
              JSON.stringify(distressFields.distress);

          if (needsHistoryUpdate || needsTypeUpdate || needsDistressUpdate) {
            const propertyDocRef = doc(
              db,
              `artifacts/${appId}/users/${userId}/trackedProperties`,
              prop.id
            );

            // Track what changes are being made
            const changes = [];
            const propertyRef =
              formatPropertyReference(prop) ||
              `Property #${prop.id.substring(0, 8)}`;
            const propertyTitle = prop.title
              ? `${prop.title.substring(0, 50)}${
                  prop.title.length > 50 ? "..." : ""
                } (${propertyRef})`
              : propertyRef;

            // Prepare update data with proper null/undefined handling
            const updateData = {
              lastModifiedTimestamp: serverTimestamp(),
            };

            // Add history update if needed
            if (needsHistoryUpdate) {
              const newHistorySnapshot = {
                price: newPrice,
                reportedPublishedDate: newApiPublishedDt || "N/A",
                reportedUpdateDate: newApiUpdatedDt || "N/A",
                snapshotTimestamp: Timestamp.now(),
              };
              const updatedHistory = [newHistorySnapshot, ...sortedHistory];

              updateData.history = updatedHistory;
              updateData.lastKnownPrice = newPrice;
              updateData.lastReportedUpdateDate = newApiUpdatedDt || "N/A";

              // Track price changes
              if (latestHistoryEntry && newPrice !== latestHistoryEntry.price) {
                const oldPrice = latestHistoryEntry.price;
                const priceDiff = newPrice - oldPrice;
                const priceChange =
                  priceDiff > 0
                    ? `+€${priceDiff.toLocaleString()}`
                    : `-€${Math.abs(priceDiff).toLocaleString()}`;
                changes.push(
                  `Price: €${oldPrice.toLocaleString()} → €${newPrice.toLocaleString()} (${priceChange})`
                );
              } else if (!latestHistoryEntry) {
                changes.push(`Price: €${newPrice.toLocaleString()}`);
              }

              // Track date changes
              if (
                latestHistoryEntry &&
                newApiUpdatedDt &&
                newApiUpdatedDt !==
                  (latestHistoryEntry.reportedUpdateDate || "")
              ) {
                changes.push(`Updated: ${newApiUpdatedDt}`);
              } else if (!latestHistoryEntry && newApiUpdatedDt) {
                changes.push(`Updated: ${newApiUpdatedDt}`);
              }
            }

            // Track image changes using normalized imageURL
            const newImageURL = fetchedData.imageURL || prop.imageURL || "";
            if (newImageURL !== (prop.imageURL || "") && newImageURL) {
              changes.push("New photos added");
            }

            // Track property type changes
            if (needsTypeUpdate) {
              const oldType = [];
              const newType = [];
              if (prop.forSale) oldType.push("Sale");
              if (prop.forRent) oldType.push("Rent");
              if (forSale) newType.push("Sale");
              if (forRent) newType.push("Rent");

              if (oldType.join("+") !== newType.join("+")) {
                changes.push(
                  `Type: ${oldType.join("+")} → ${newType.join("+")}`
                );
              }
            }

            // Update all fields from normalized data with safe defaults
            updateData.title = fetchedData.title || prop.title || "";
            updateData.area = fetchedData.area || prop.area || "";
            updateData.bedrooms = fetchedData.bedrooms || prop.bedrooms || "";
            updateData.bathrooms = fetchedData.bathrooms || prop.bathrooms || "";
            updateData.startPrice = fetchedData.startPrice || prop.startPrice || "";
            updateData.imageURL = newImageURL;
            updateData.latitude = fetchedData.latitude ?? prop.latitude ?? null;
            updateData.longitude = fetchedData.longitude ?? prop.longitude ?? null;
            updateData.pets = fetchedData.pets || prop.pets || "";
            updateData.furnishing = fetchedData.furnishing || prop.furnishing || "";
            updateData.parking = fetchedData.parking || prop.parking || "";
            updateData.constructionYear = fetchedData.constructionYear || prop.constructionYear || "";
            updateData.onlineViewing = fetchedData.onlineViewing || prop.onlineViewing || "";
            updateData.landlordName = fetchedData.landlordName || prop.landlordName || "";
            updateData.landlordJoined = fetchedData.landlordJoined || prop.landlordJoined || "";
            updateData.ownerAdCount = fetchedData.ownerAdCount || prop.ownerAdCount || "";
            updateData.cityId = fetchedData.cityId || prop.cityId || "";
            // Preserve existing notes, but append richer scraped notes when they
            // are significantly longer than current notes.
            updateData.notes = mergeNotesForRefetch(prop.notes || "", notesFromApi);

            // Always update property type
            updateData.forSale = forSale;
            updateData.forRent = forRent;

            // Persist distressed-asset signals
            Object.assign(updateData, distressFields);

            batch.update(propertyDocRef, updateData);
            updatedCount++;

            // Add to updated properties list
            updatedProperties.push({
              propertyId: prop.id,
              propertyTitle: propertyTitle,
              propertyReference: propertyRef,
              changeType:
                changes.length > 0
                  ? changes.some((c) => c.includes("Price:"))
                    ? "price"
                    : changes.some((c) => c.includes("photos"))
                    ? "photos"
                    : changes.some((c) => c.includes("Updated:"))
                    ? "date"
                    : changes.some((c) => c.includes("Type:"))
                    ? "type"
                    : "general"
                  : "general",
              changeDescription:
                changes.length > 0
                  ? changes.join(", ")
                  : "Property data refreshed",
              oldValue: latestHistoryEntry?.price?.toString() || "",
              newValue: newPrice.toString(),
            });
          }
        } else {
          const propertyRef =
            formatPropertyReference(prop) ||
            `Property #${prop.id.substring(0, 8)}`;
          const propertyTitle = prop.title
            ? `${prop.title.substring(0, 30)}${
                prop.title.length > 30 ? "..." : ""
              } (${propertyRef})`
            : propertyRef;
          errorsEncountered.push(`${propertyTitle}: No data returned`);
        }
      } catch (fetchError) {
        console.error(`Error fetching property ${prop.url}:`, fetchError);
        checkedCount++;

        // Create user-friendly error message
        let errorMsg;
        if (fetchError.message.includes("not found")) {
          // Use the new platform-aware error formatting
          errorMsg = formatPropertyNotFoundError(prop);
        } else {
          // For other errors, use consistent formatting
          const propertyRef =
            formatPropertyReference(prop) ||
            `Property #${prop.id.substring(0, 8)}`;
          const propertyTitle = prop.title
            ? `${prop.title.substring(0, 30)}${
                prop.title.length > 30 ? "..." : ""
              } (${propertyRef})`
            : propertyRef;

          if (fetchError.message.includes("proxy services")) {
            errorMsg = `${propertyTitle}: Proxy services unavailable`;
          } else if (fetchError.message.includes("timed out")) {
            errorMsg = `${propertyTitle}: Request timed out`;
          } else {
            errorMsg = `${propertyTitle}: ${fetchError.message.substring(
              0,
              50
            )}`;
          }
        }

        errorsEncountered.push(errorMsg);
      }
    }

    try {
      await batch.commit();
    } catch (batchError) {
      console.error("Error committing batch re-fetch updates:", batchError);
      errorsEncountered.push("Error saving some updates to database.");
    }

    // Calculate sync duration
    const duration = ((Date.now() - startTime) / 1000).toFixed(1);
    const unchangedCount =
      checkedCount - updatedCount - errorsEncountered.length;

    // Build enhanced summary
    let summaryMsg = `Re-fetch complete. Checked ${checkedCount} properties in ${duration}s.\n\n`;

    // Updated properties section
    if (updatedProperties.length > 0) {
      summaryMsg += `✅ Updated (${updatedProperties.length}):\n`;
      updatedProperties.forEach((prop) => {
        // Handle both old format (changes array) and new format (changeDescription string)
        const changeText = Array.isArray(prop.changes) 
          ? prop.changes.join(", ") 
          : (prop.changeDescription || "Property data refreshed");
        const title = prop.propertyTitle || prop.title || "Property";
        summaryMsg += `• ${title} - ${changeText}\n`;
      });
      summaryMsg += "\n";
    }

    // Errors section
    if (errorsEncountered.length > 0) {
      summaryMsg += `❌ Errors (${errorsEncountered.length}):\n`;
      errorsEncountered.forEach((error) => {
        summaryMsg += `• ${error}\n`;
      });
      summaryMsg += "\n";
    }

    // No changes section
    if (unchangedCount > 0) {
      summaryMsg += `ℹ️ No changes: ${unchangedCount} properties`;
    }

    const summary = summaryMsg.trim();

    // Return both summary and structured sync data
    return {
      summary,
      syncData: {
        duration,
        totalChecked: checkedCount,
        totalUpdated: updatedCount,
        totalErrors: errorsEncountered.length,
        changes: updatedProperties,
        errors: errorsEncountered.map((error) => {
          // Extract property info from error message
          const parts = error.split(":");
          const propertyInfo = parts[0] || error;
          const errorMsg = parts.slice(1).join(":").trim() || error;

          return {
            propertyTitle: propertyInfo,
            propertyReference: propertyInfo.includes("(")
              ? propertyInfo.split("(")[1]?.replace(")", "") || propertyInfo
              : propertyInfo,
            errorType: error.includes("expired")
              ? "expired"
              : error.includes("timeout")
              ? "timeout"
              : "network",
            errorMessage: errorMsg || error,
          };
        }),
      },
    };
  };

  const refetchProperty = async (propertyId, fetchPropertyData) => {
    if (!db || !userId) {
      throw new Error("Database or user not available.");
    }

    const prop = properties.find((p) => p.id === propertyId);
    if (!prop) throw new Error("Property not found in local state.");

    const fetchedData = await fetchPropertyData(prop.url);
    if (!fetchedData) throw new Error("No data returned from fetch.");

    const newPrice = fetchedData.price || 0;
    const newApiPublishedDt = fetchedData.publishedDate || "";
    const newApiUpdatedDt = fetchedData.updatedDate || "";
    const notesFromApi =
      (fetchedData.title ? `Title: ${fetchedData.title}\n` : "") +
      (fetchedData.description
        ? `Description: ${fetchedData.description}`
        : "");

    const sortedHistory = (prop.history || [])
      .slice()
      .sort(
        (a, b) =>
          (b.snapshotTimestamp?.toDate?.() || new Date(0)) -
          (a.snapshotTimestamp?.toDate?.() || new Date(0))
      );
    const latestHistoryEntry =
      sortedHistory.length > 0 ? sortedHistory[0] : null;

    let needsHistoryUpdate = false;
    if (!latestHistoryEntry) {
      needsHistoryUpdate = true;
    } else {
      if (newPrice !== latestHistoryEntry.price) needsHistoryUpdate = true;
      if (
        newApiUpdatedDt &&
        newApiUpdatedDt !== (latestHistoryEntry.reportedUpdateDate || "")
      )
        needsHistoryUpdate = true;
    }

    const forSale = fetchedData.forSale || false;
    const forRent = fetchedData.forRent || false;
    const needsTypeUpdate =
      prop.forSale !== forSale ||
      prop.forRent !== forRent ||
      prop.forSale === undefined ||
      prop.forRent === undefined;

    const changes = [];

    const updateData = {
      lastModifiedTimestamp: serverTimestamp(),
    };

    if (needsHistoryUpdate) {
      const newHistorySnapshot = {
        price: newPrice,
        reportedPublishedDate: newApiPublishedDt || "N/A",
        reportedUpdateDate: newApiUpdatedDt || "N/A",
        snapshotTimestamp: Timestamp.now(),
      };
      updateData.history = [newHistorySnapshot, ...sortedHistory];
      updateData.lastKnownPrice = newPrice;
      updateData.lastReportedUpdateDate = newApiUpdatedDt || "N/A";

      if (latestHistoryEntry && newPrice !== latestHistoryEntry.price) {
        const priceDiff = newPrice - latestHistoryEntry.price;
        const priceChange =
          priceDiff > 0
            ? `+€${priceDiff.toLocaleString()}`
            : `-€${Math.abs(priceDiff).toLocaleString()}`;
        changes.push(
          `Price: €${latestHistoryEntry.price.toLocaleString()} → €${newPrice.toLocaleString()} (${priceChange})`
        );
      }
    }

    if (needsTypeUpdate) {
      changes.push("Listing type updated");
    }

    const newImageURL = fetchedData.imageURL || prop.imageURL || "";

    updateData.title = fetchedData.title || prop.title || "";
    updateData.area = fetchedData.area || prop.area || "";
    updateData.bedrooms = fetchedData.bedrooms || prop.bedrooms || "";
    updateData.bathrooms = fetchedData.bathrooms || prop.bathrooms || "";
    updateData.startPrice = fetchedData.startPrice || prop.startPrice || "";
    updateData.imageURL = newImageURL;
    updateData.latitude = fetchedData.latitude ?? prop.latitude ?? null;
    updateData.longitude = fetchedData.longitude ?? prop.longitude ?? null;
    updateData.pets = fetchedData.pets || prop.pets || "";
    updateData.furnishing = fetchedData.furnishing || prop.furnishing || "";
    updateData.parking = fetchedData.parking || prop.parking || "";
    updateData.constructionYear =
      fetchedData.constructionYear || prop.constructionYear || "";
    updateData.onlineViewing =
      fetchedData.onlineViewing || prop.onlineViewing || "";
    updateData.landlordName =
      fetchedData.landlordName || prop.landlordName || "";
    updateData.landlordJoined =
      fetchedData.landlordJoined || prop.landlordJoined || "";
    updateData.ownerAdCount =
      fetchedData.ownerAdCount || prop.ownerAdCount || "";
    updateData.cityId = fetchedData.cityId || prop.cityId || "";
    updateData.notes = mergeNotesForRefetch(prop.notes || "", notesFromApi);
    updateData.forSale = forSale;
    updateData.forRent = forRent;

    // Persist distressed-asset signals (so badges render)
    Object.assign(updateData, extractDistressFields(fetchedData, prop));

    await updateDoc(
      doc(
        db,
        `artifacts/${appId}/users/${userId}/trackedProperties`,
        prop.id
      ),
      updateData
    );

    return {
      updated: needsHistoryUpdate || needsTypeUpdate,
      changes,
    };
  };

  return {
    properties,
    isLoading,
    error,
    addProperty,
    updateProperty,
    deleteProperty,
    toggleFavorite,
    refetchAllProperties,
    refetchProperty,
  };
};
